import { useEffect } from 'react';

import { useAlert } from '@inbody/core';

import {
  selectZipBusy,
  useCartDownloadStore,
} from '../store/useCartDownloadStore';

import { useDownloadJob, useRemoveCartItems } from '@/pages/v2/api';
import { triggerBrowserDownload } from '@/pages/v2/lib';

/**
 * 장바구니 ZIP 작업 전역 감시자 — v2 레이아웃(GenericLayoutV2)에 한 번 마운트된다.
 *
 * 장바구니에서 ZIP을 요청한 뒤 다른 화면으로 가도 폴링을 이어가 완료되면 내려받는다
 * (CART-21 — 장바구니를 떠날 때는 확인 창, 새로고침·탭 닫기는 브라우저 경고를 띄운다).
 * 결과 안내는 토스트 1개로 합친다 — 권한 제외 경고가 'ZIP 다운로드를
 * 시작합니다.'에 곧바로 가려지던 문제(CART-07).
 *
 * 완료 시 받은 항목만 장바구니에서 뺀다(다운로드 시작 시점 · 권한 제외분은 작업 생성
 * 응답의 skippedFileIds로 걸러 남긴다 — F8). 일부만 받았다면 남은 항목은 다시 기본
 * 선택으로 돌려 다음 다운로드를 바로 할 수 있게 한다(CART-05).
 */
export const CartDownloadWatcherSection = () => {
  const { toastAlert } = useAlert();
  const { jobId, removeOnDone, finishJob, setUnchecked } =
    useCartDownloadStore();
  const { data: job } = useDownloadJob(jobId);
  // 사용자가 누른 게 아니므로 토스트 없이 뺀다
  const { mutateAsync: removeDownloaded } = useRemoveCartItems({
    silent: true,
  });

  // ZIP을 만드는 중 새로고침·탭 닫기는 작업 id를 잃어 받을 수 없다 — 브라우저 경고(CART-21).
  // 요청을 보낸 순간부터 켠다(작업 id는 응답 뒤에 생긴다).
  // (v2 안의 화면 이동은 이어 받으므로 장바구니 화면의 CartLeaveGuardSection만 확인한다)
  const zipBusy = useCartDownloadStore(selectZipBusy);
  useEffect(() => {
    if (!zipBusy) return;
    const warn = (event: BeforeUnloadEvent) => {
      // 완료 시 ZIP 링크(교차 출처 SAS라 download 속성이 무시돼 페이지 이동으로 처리)도
      // beforeunload를 부른다 — 그 전에 작업을 끝내 두므로 지금 상태로 다시 확인한다.
      // (effect 정리는 다음 렌더 뒤라 리스너가 아직 붙어 있다)
      if (!selectZipBusy(useCartDownloadStore.getState())) return;
      event.preventDefault();
      // 일부 브라우저는 returnValue가 있어야 확인 창을 띄운다
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [zipBusy]);

  useEffect(() => {
    if (!jobId || !job) return;
    const skipped = job.skippedCount;
    const skippedText =
      skipped > 0 ? ` 권한이 없는 ${skipped}개 항목은 제외했습니다.` : '';

    if (job.status === 'Completed') {
      // 다운로드 링크를 열기 전에 끝내야 새로고침 경고가 뜨지 않는다 (removeOnDone은 이 렌더 값)
      finishJob();
      if (!job.url) {
        // 완료인데 url이 없으면 받을 수 없으므로 장바구니에서 빼지 않고 상태만 푼다
        toastAlert.error(
          '다운로드 링크를 받지 못했습니다. 잠시 후 다시 시도해주세요.',
        );
      } else {
        triggerBrowserDownload(job.url, job.fileName ?? undefined);
        if (removeOnDone.length > 0) {
          void removeDownloaded({ fileIds: removeOnDone });
          setUnchecked([]);
          if (skipped > 0) {
            toastAlert.warning(
              `ZIP 다운로드를 시작합니다. 받은 ${removeOnDone.length}개는 장바구니에서 뺐고, 권한이 없는 ${skipped}개는 남겨 두었습니다.`,
            );
          } else {
            toastAlert.success(
              `ZIP 다운로드를 시작합니다. 받은 ${removeOnDone.length}개 항목은 장바구니에서 뺐습니다.`,
            );
          }
        } else if (skipped > 0) {
          toastAlert.warning(`ZIP 다운로드를 시작합니다.${skippedText}`);
        } else {
          toastAlert.success('ZIP 다운로드를 시작합니다.');
        }
      }
    } else if (job.status === 'Failed') {
      toastAlert.error(job.errorMessage ?? 'ZIP 생성에 실패했습니다.');
      finishJob();
    } else if (job.status === 'Empty') {
      toastAlert.warning(`다운로드할 수 있는 항목이 없습니다.${skippedText}`);
      finishJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, job?.status, job?.url]);

  return null;
};
